import React from 'react';
import type { ColDef, GridApi, ICellRendererParams, RowClassParams, RowStyle } from 'ag-grid-community';
import { compose } from 'recompose';
import { inject, observer } from 'mobx-react';
import { withStyles, withTheme } from '@material-ui/core';
import { withSnackbar } from 'notistack';
import StatusBar from '@dataGrid/components/StatusBar';
import appConstants from '@appConstants';
import { withUserProfile } from '../../../../../userProfile/userProfileContext';
import { MasterDetailGrid } from '../../../../dataGrid/MasterDetails';
import GridConfig from './GridConfig';
import styles from './styles';
import { buildTrailerMoveMasterRows, computeDetailPanelHeight } from './utils';
import {
  toTrailerMoveSelection,
  doesTrailerMovePassFilters,
  constructStatusBar,
  doesExternalFilterPass,
  getGroupRowStyle,
  normalizeGroupId,
} from './filtering/filterUtils';
import { loadSwapBoard } from './swapBoardService';
import { DEFAULT_TRAILER_MOVES_FILTER, TRAILER_MOVES_PANEL_FILTERS } from './Filter/constants';
import TrailerMovesDetailGrid from './components/TrailerMovesDetailGrid';
import type { TrailerMovesDetailGridProps } from './components/TrailerMovesDetailGrid';
import { DETAIL_PANEL_ANCHOR_FIELD } from './constants';
import type {
  SwapBoardDispatchStore,
  SwapBoardTrailer,
  TrailerMoveMasterRow,
  TrailerMoveFilterOption,
  GlEventHub,
  GoldenLayoutContainer,
} from './types';
import type { AppTheme } from './styles';

interface TrailerMovesState {
  searchText: string;
  headerFilter: TrailerMoveFilterOption;
  panelFilter: string;
  selectedDetailPickupId: string | null;
  clearSelectionCount: number;
}

interface TrailerMovesProps {
  DispatchStore: SwapBoardDispatchStore;
  userData?: {
    userProfile?: {
      terminal?: { code?: string; offset?: string };
      columnPref?: Record<string, unknown>;
    };
  };
  glEventHub?: GlEventHub;
  glContainer?: GoldenLayoutContainer;
  classes: Record<string, string>;
  theme?: AppTheme;
}

@inject('DispatchStore')
@observer
class TrailerMoves extends React.Component<TrailerMovesProps, TrailerMovesState> {
  gridConfig: GridConfig;
  gridApi: GridApi | null;
  gridHeaderId: string;
  _masterRows?: TrailerMoveMasterRow[];

  constructor(props: TrailerMovesProps) {
    super(props);
    this.gridConfig = new GridConfig({
      userProfile: props.userData?.userProfile,
    });
    this.gridApi = null;
    this.state = {
      searchText: '',
      headerFilter: DEFAULT_TRAILER_MOVES_FILTER,
      panelFilter: TRAILER_MOVES_PANEL_FILTERS.all,
      selectedDetailPickupId: null,
      clearSelectionCount: 0,
    };
    this.gridHeaderId = `gridHeader_${props.glContainer?._config?.id}`;
  }

  componentDidMount() {
    const terminalCode = this.props.userData?.userProfile?.terminal?.code;
    this.loadSwapBoardIfNeeded(terminalCode);
    this.props.glEventHub?.on(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub?.on(
      `${this.gridHeaderId}_selectFilterSelected`,
      this.onHeaderFilterChanged
    );
    this.props.glContainer?.layoutManager?.on?.(
      'activeContentItemChanged',
      this.onStackTabChanged
    );
  }

  componentDidUpdate(prevProps: TrailerMovesProps) {
    const terminalCode = this.props.userData?.userProfile?.terminal?.code;
    const prevTerminalCode = prevProps.userData?.userProfile?.terminal?.code;
    if (!prevTerminalCode && terminalCode) {
      this.loadSwapBoardIfNeeded(terminalCode);
    }
    if (prevProps.DispatchStore.swapTrailers !== this.props.DispatchStore.swapTrailers) {
      this.refreshGridFilter();
    }
  }

  loadSwapBoardIfNeeded = (terminalCode?: string) => {
    const { DispatchStore } = this.props;
    if (
      terminalCode &&
      DispatchStore.swapBoardGridStatus === appConstants.GRID_STATUS.LOADING &&
      DispatchStore.swapTrailers.length === 0
    ) {
      loadSwapBoard(DispatchStore, terminalCode);
    }
  };

  componentWillUnmount() {
    this.gridApi = null;
    this.props.glEventHub?.off(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub?.off(
      `${this.gridHeaderId}_selectFilterSelected`,
      this.onHeaderFilterChanged
    );
    this.props.glContainer?.layoutManager?.off?.(
      'activeContentItemChanged',
      this.onStackTabChanged
    );
    this.resetTrailerMovesTabState();
  }

  onStackTabChanged = (contentItem: { config?: { id?: string } }) => {
    if (contentItem?.config?.id !== appConstants.gridNames.TrailerMovesGrid) {
      this.resetTrailerMovesTabState();
    }
  };

  resetTrailerMovesTabState = () => {
    this.gridApi?.deselectAll?.();
    this.props.glEventHub?.emit('UpdateDispatchStore', 'updateSelectedTrailerMove', [null]);
    this.props.glEventHub?.emit(this.gridHeaderId, '');
    this.props.glEventHub?.emit(
      `${this.gridHeaderId}_resetFilter`,
      DEFAULT_TRAILER_MOVES_FILTER
    );

    this.setState(
      (prevState) => ({
        headerFilter: DEFAULT_TRAILER_MOVES_FILTER,
        panelFilter: TRAILER_MOVES_PANEL_FILTERS.all,
        searchText: '',
        selectedDetailPickupId: null,
        clearSelectionCount: prevState.clearSelectionCount + 1,
      }),
      this.refreshGridFilter
    );
  };

  clearSelection = () => {
    this.gridApi?.deselectAll?.();
    this.props.glEventHub?.emit('UpdateDispatchStore', 'updateSelectedTrailerMove', [null]);
    this.setState((prevState) => ({
      selectedDetailPickupId: null,
      clearSelectionCount: prevState.clearSelectionCount + 1,
    }));
  };

  onSearchTextChanged = (searchText: string) => {
    this.setState({ searchText: searchText || '' }, this.refreshGridFilter);
  };

  refreshGridFilter = () => {
    this.gridApi?.onFilterChanged?.();
    this.gridApi?.resetRowHeights?.();
  };

  onHeaderFilterChanged = ({ option }: { option?: TrailerMoveFilterOption }) => {
    if (!option?.filterFunc) {
      return;
    }
    this.clearSelection();
    this.setState({ headerFilter: option, panelFilter: TRAILER_MOVES_PANEL_FILTERS.all }, this.refreshGridFilter);
  };

  onPanelFilterChanged = (filterCondition?: string) => {
    const nextFilter = filterCondition || TRAILER_MOVES_PANEL_FILTERS.all;
    this.setState({ panelFilter: nextFilter }, this.refreshGridFilter);
  };

  getApiGroups = () => this.props.DispatchStore.swapTrailers;

  getMasterRows = () => this._masterRows ?? [];

  getFilterProps = () => ({
    headerFilter: this.state.headerFilter,
    panelFilter: this.state.panelFilter,
    searchText: this.state.searchText,
    terminalOffset: this.props.DispatchStore?.terminalOffset,
  });

  getFilteredChildCount = (masterRow: TrailerMoveMasterRow) => {
    const filterProps = this.getFilterProps();
    return (masterRow.trailers ?? []).filter((trailer) =>
      doesTrailerMovePassFilters({
        row: trailer,
        ...filterProps,
      })
    ).length;
  };

  getDetailRowHeight = (masterRow: TrailerMoveMasterRow) => {
    const count = this.getFilteredChildCount(masterRow);
    return count > 0 ? computeDetailPanelHeight(count) : 0;
  };

  shouldShowDetailRow = (masterRow: TrailerMoveMasterRow) => this.getFilteredChildCount(masterRow) > 0;

  doesExternalFilterPass = (node: { data?: TrailerMoveMasterRow }) =>
    doesExternalFilterPass({
      node,
      allRows: this._masterRows ?? [],
      headerFilter: this.state.headerFilter,
      panelFilter: this.state.panelFilter,
      searchText: this.state.searchText,
      terminalOffset: this.props.DispatchStore?.terminalOffset,
    });

  isExternalFilterPresent = () =>
    Boolean(
      this.state.headerFilter?.value ||
        this.state.panelFilter !== TRAILER_MOVES_PANEL_FILTERS.all ||
        this.state.searchText
    );

  syncDriverGridSelection = (selectedRow: SwapBoardTrailer | null) => {
    const { glEventHub } = this.props;
    const terminalOffset = this.props.userData?.userProfile?.terminal?.offset;
    const selection = toTrailerMoveSelection(selectedRow, terminalOffset);

    glEventHub?.emit('UpdateDispatchStore', 'updateSelectedTrailerMove', [selection]);

    if (selection?.driverId) {
      glEventHub?.emit('SelectDriverRoutes', [selection.driverId]);
    }
    glEventHub?.emit(appConstants.Event.SELECT_PICKUP_ROUTES, true);
  };

  onDetailSelectionChanged = (selectedRow: SwapBoardTrailer | null) => {
    const pickupId = selectedRow?.pickupId?.toUpperCase?.() ?? null;
    if (pickupId !== this.state.selectedDetailPickupId) {
      this.setState({ selectedDetailPickupId: pickupId });
    }
    this.syncDriverGridSelection(selectedRow);
  };

  onGridReady = ({ api }: { api: GridApi }) => {
    this.gridApi = api;
    this.refreshGridFilter();
  };

  constructStatusBar = (filteredData: (TrailerMoveMasterRow | SwapBoardTrailer)[]) =>
    constructStatusBar({
      allRows: this.getMasterRows(),
      headerFilter: this.state.headerFilter,
      filteredData,
      isColumnFilterPresent: this.gridApi?.isColumnFilterPresent?.(),
      terminalOffset: this.props.DispatchStore?.terminalOffset,
    });

  getRowStyle = (_params: RowClassParams<TrailerMoveMasterRow>): RowStyle =>
    getGroupRowStyle(this.props.theme) as RowStyle;

  renderDetailGrid = (detailProps: {
    data: TrailerMoveMasterRow;
    context?: TrailerMovesDetailGridProps['context'];
  }) => (
    <TrailerMovesDetailGrid
      data={detailProps.data}
      context={detailProps.context}
      layoutContainer={this.props.glContainer}
      columnDefs={this.gridConfig.config.detailColumns}
      defaultColDef={this.gridConfig.config.detailDefaultColDef}
      clearSelectionCount={this.state.clearSelectionCount}
      selectedDetailPickupId={this.state.selectedDetailPickupId}
      onDetailSelectionChanged={this.onDetailSelectionChanged}
      theme={this.props.theme}
      {...this.getFilterProps()}
    />
  );

  render() {
    this._masterRows = buildTrailerMoveMasterRows(this.getApiGroups());
    const masterRows = this._masterRows;
    const gridStatus = this.props.DispatchStore.swapBoardGridStatus;

    return (
      <div className={this.props.classes.dataGrid}>
        <MasterDetailGrid<TrailerMoveMasterRow>
          defaultColDef={this.gridConfig.config.defaultColDef}
          columnDefs={this.gridConfig.config.columns as ColDef<TrailerMoveMasterRow>[]}
          config={{ title: this.gridConfig.config.title }}
          rowSelection={this.gridConfig.config.rowSelection as 'single'}
          rowDeselection={this.gridConfig.config.rowDeselection}
          rowData={masterRows}
          onGridReady={this.onGridReady}
          panelLabel={(filteredData: (TrailerMoveMasterRow | SwapBoardTrailer)[]) => (
            <StatusBar
              data={this.constructStatusBar(filteredData)}
              filterGridData={this.onPanelFilterChanged}
            />
          )}
          layoutContainer={this.props.glContainer}
          getRowId={(row) => normalizeGroupId(row.rmaxShipperCode) ?? ''}
          getDetailRowHeight={this.getDetailRowHeight}
          shouldShowDetailRow={this.shouldShowDetailRow}
          expandColumnId="isMergedPickup"
          detailPanelAnchorField={DETAIL_PANEL_ANCHOR_FIELD}
          detailPanelLeftOffsetFallback={appConstants.columnWidth.MERGED_PICKUP_ICON}
          showErrorMessage={gridStatus}
          isRowSelectable={() => false}
          suppressRowClickSelection
          doesExternalFilterPass={this.doesExternalFilterPass}
          isExternalFilterPresent={this.isExternalFilterPresent}
          getRowStyle={this.getRowStyle}
          detailCellRenderer={this.renderDetailGrid as React.ComponentType<ICellRendererParams>}
          suppressRowVirtualisation={masterRows.length < 300}
        />
      </div>
    );
  }
}

export default compose(
  withUserProfile,
  withSnackbar,
  withTheme(),
  withStyles(styles as Parameters<typeof withStyles>[0])
)(TrailerMoves);
